import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconChevron } from '@components/icons';
import { ChannelLimitInput } from '@components/input-select-channels/components/channel-limit-input';
import classNames from 'classnames';

import { HierarchicalChannelLimitsProps } from './hierarchical-channel-limits.type';

export const HierarchicalChannelLimits = ({
  nestedCheckboxData,
  selectedChannels,
  teamQuantities,
  disabled,
  onChannelLimitChange,
  onTeamLimitChange,
  getChannelError,
}: HierarchicalChannelLimitsProps) => {
  const channelLookup = useMemo(
    () => new Map(selectedChannels.map((ch, idx) => [ch.id, { channel: ch, selectedIndex: idx }])),
    [selectedChannels]
  );

  const teamLookup = useMemo(() => new Map(teamQuantities.map((tq) => [tq.teamId, tq])), [teamQuantities]);

  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const hasComputedInitialExpansion = useRef(false);

  // Compute initial expansion when channel data is ready:
  // - Any channel in the team has a value → open
  // - No channels have values → closed
  //
  // Guards (three independent API responses must arrive before computing):
  // 1. nestedCheckboxData — team hierarchy (from eventTeams)
  // 2. channelLookup — selected channels (from admissionChannels)
  // 3. anyChannelHasValue — enriched values (from activeChannels)
  useEffect(() => {
    if (hasComputedInitialExpansion.current) return;
    if (nestedCheckboxData.length === 0) return; // Team hierarchy not loaded yet
    if (channelLookup.size === 0) return; // Channels not loaded yet

    const anyChannelHasValue = [...channelLookup.values()].some((entry) => entry.channel.value !== '');
    if (!anyChannelHasValue) return; // Channel values not enriched yet

    hasComputedInitialExpansion.current = true;
    const expanded = new Set<string>();
    nestedCheckboxData.forEach((item) => {
      if (item.children.length > 0) {
        const hasChildWithValue = item.children.some((child) => {
          const entry = channelLookup.get(child.id);
          return entry && entry.channel.value !== '';
        });

        if (hasChildWithValue) expanded.add(item.id);
      }
    });
    setExpandedTeams(expanded);
  }, [nestedCheckboxData, channelLookup]);

  const toggleTeam = useCallback((teamId: string) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }, []);

  // Filter to only show teams that have at least one selected channel
  const visibleItems = useMemo(
    () =>
      nestedCheckboxData.filter((item) => {
        if (item.children.length > 0) {
          return item.children.some((child) => channelLookup.has(child.id));
        }
        return channelLookup.has(item.id);
      }),
    [nestedCheckboxData, channelLookup]
  );

  // Auto-expand collapsed teams when a child channel has an error
  useEffect(() => {
    visibleItems.forEach((item) => {
      if (item.children.length > 0) {
        const hasChildWithError = item.children.some((child) => {
          const entry = channelLookup.get(child.id);
          return entry && getChannelError(entry.selectedIndex);
        });
        if (hasChildWithError) {
          setExpandedTeams((prev) => {
            if (prev.has(item.id)) return prev;
            return new Set([...prev, item.id]);
          });
        }
      }
    });
  }, [visibleItems, channelLookup, getChannelError]);

  return (
    <div className='flex flex-col gap-y-4' data-testid='hierarchical-channel-limits'>
      {visibleItems.map((item) => {
        const isTeam = item.children.length > 0;

        if (isTeam) {
          const isExpanded = expandedTeams.has(item.id);
          const teamQuantity = teamLookup.get(item.id);

          return (
            <div key={item.id} data-testid={`team-row-${item.id}`}>
              <div className='flex items-center gap-x-2'>
                <button
                  type='button'
                  onClick={() => toggleTeam(item.id)}
                  className='shrink-0'
                  aria-expanded={isExpanded}
                  aria-label={item.name}
                  data-testid={`team-chevron-${item.id}`}>
                  <IconChevron
                    className={classNames('w-3 h-3 stroke-2 transition duration-200 ease-in-out', {
                      '-rotate-90': isExpanded,
                      '-rotate-180': !isExpanded,
                    })}
                  />
                </button>
                <ChannelLimitInput
                  name={item.name}
                  value={teamQuantity?.quantity ?? ''}
                  disabled={disabled}
                  testId={`team-limit-${item.id}`}
                  onChange={(val) => onTeamLimitChange(item.id, val)}
                />
              </div>

              {isExpanded && (
                <div className='ml-8 mt-3 flex flex-col gap-y-3'>
                  {item.children.map((child) => {
                    const entry = channelLookup.get(child.id);
                    if (!entry) return null;
                    const { channel, selectedIndex } = entry;
                    const errorMessage = getChannelError(selectedIndex);

                    return (
                      <ChannelLimitInput
                        key={child.id}
                        name={child.name}
                        value={channel.value}
                        placeholder='∞'
                        hasError={!!errorMessage}
                        errorMessage={errorMessage}
                        disabled={disabled}
                        testId={`channel-limit-${child.id}`}
                        onChange={(val) => onChannelLimitChange(channel.originalIndex, val)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        // Standalone channel (no team)
        const entry = channelLookup.get(item.id);
        if (!entry) return null;
        const { channel, selectedIndex } = entry;
        const errorMessage = getChannelError(selectedIndex);

        return (
          <ChannelLimitInput
            key={item.id}
            name={item.name}
            value={channel.value}
            placeholder='∞'
            hasError={!!errorMessage}
            errorMessage={errorMessage}
            disabled={disabled}
            testId={`channel-limit-${item.id}`}
            onChange={(val) => onChannelLimitChange(channel.originalIndex, val)}
          />
        );
      })}
    </div>
  );
};
